import { MaybeRefOrGetter, computed, toRef, toValue, watch } from 'vue';
import { usePagerQuery } from '@precon-tools-suite-by-shared/shared-graphql-client-vue-pagination';
import { TeamsTagsDocument } from './useTeamsTags.generated';
import {
    TagOrderByClause,
    TagWhereClause,
} from '@precon-tools-suite-by-shared/precon-graphql-types';

export type TagSet = 'usable' | 'currentTeam' | 'ginoTeam';
export const tagSetOptions: TagSet[] = ['usable', 'currentTeam', 'ginoTeam'];

export interface UseCurrentTeamsTagsOptions {
    first: MaybeRefOrGetter<number>;
    after: MaybeRefOrGetter<string>;
    orderBy: MaybeRefOrGetter<TagOrderByClause[]>;
    where: MaybeRefOrGetter<TagWhereClause[]>;
    search: MaybeRefOrGetter<string | undefined>;
    set: MaybeRefOrGetter<TagSet>;
}

export function useCurrentTeamsTagsQuery(options: UseCurrentTeamsTagsOptions) {
    const set = toRef(options.set);

    const queryVariables = computed(() => {
        return {
            first: toValue(options.first),
            after: toValue(options.after),
            orderBy: toValue(options.orderBy),
            where: toValue(options.where),
            search: toValue(options.search),
            usable: set.value === 'usable',
            currentTeam: set.value === 'currentTeam',
            ginoTeam: set.value === 'ginoTeam',
        };
    });

    watch(queryVariables, () => refetch(queryVariables.value), { deep: true });

    const {
        loading,
        count,
        nodes,
        nodesWithPlaceholders,
        fetchNextPage,
        nextPageLoading,
        onError,
        onResult,
        refetch,
    } = usePagerQuery({
        queryDocument: TeamsTagsDocument,
        connection: (result) => {
            switch (set.value) {
                case 'usable':
                    return result.me?.team?.usableTags;
                case 'currentTeam':
                    return result.me?.team?.ownedTags;
                case 'ginoTeam':
                    return result.commonGinoTeam?.ownedTags;
            }
        },
    });

    const refresh = () => refetch(queryVariables.value);

    return {
        loading,
        count,
        nodes,
        nodesWithPlaceholders,
        fetchNextPage,
        nextPageLoading,
        refresh,
        onError,
        onResult,
    };
}
